(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // ─── Formatage du prix ────────────────────────────────────────────────
  // Shopify stocke les prix en centimes. On affiche « 39,90 € », comme sur le
  // reste du site, plutôt que le format anglais « €39.90 ».
  const formatPrice = (cents) =>
    (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  // ─── Galerie produit : vignette ou point = changer l'image affichée ─────
  function setGalleryImage(gallery, index) {
    const images = [...gallery.querySelectorAll('[data-gallery-image]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const dots = [...gallery.querySelectorAll('[data-gallery-dot]')];

    images.forEach((img) => { img.hidden = img.dataset.index !== index; });
    thumbs.forEach((thumb) => {
      const active = thumb.dataset.index === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    dots.forEach((dot) => {
      const active = dot.dataset.index === index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function initGallery(root) {
    root.querySelectorAll('[data-product-gallery]:not([data-tapn-gallery-bound])').forEach((gallery) => {
      gallery.dataset.tapnGalleryBound = 'true';
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      const dots = [...gallery.querySelectorAll('[data-gallery-dot]')];
      if (thumbs.length < 2 && dots.length < 2) return;

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          setGalleryImage(gallery, thumb.dataset.index);
        });
      });
      dots.forEach((dot) => {
        dot.addEventListener('click', () => setGalleryImage(gallery, dot.dataset.index));
      });
    });
  }

  // ─── Coloris : amener la galerie sur l'image du coloris choisi ────────
  // Complément à initSwatches, pour les produits dont la galerie n'est pas
  // cloisonnée par variante : au lieu de masquer les vues des autres coloris,
  // on se contente d'afficher celle du coloris retenu. Les vues communes,
  // comme le verso adhésif, restent accessibles dans les vignettes.
  function initSwatchGalleryJump(root) {
    root.querySelectorAll('[data-swatch-option][data-variant-image-index]:not([data-tapn-jump-bound])').forEach((button) => {
      button.dataset.tapnJumpBound = 'true';
      button.addEventListener('click', () => {
        const gallery = button.closest('.tapn-product-hero__grid, .tapn-product-shell')?.querySelector('[data-product-gallery]');
        if (!gallery || gallery.hasAttribute('data-gallery-variant-scoped')) return;
        const index = button.dataset.variantImageIndex;
        setGalleryImage(gallery, index);
      });
    });
  }

  // ─── Sélecteur de quantité ────────────────────────────────────────────
  // Les boutons − / + pilotent le champ nombre. onChange permet aux autres
  // modules (barre collante) de réagir à chaque variation.
  function initQuantity(root) {
    root.querySelectorAll('[data-qty-control]:not([data-tapn-qty-bound])').forEach((control) => {
      control.dataset.tapnQtyBound = 'true';
      const input = control.querySelector('[data-qty-input]');
      const minus = control.querySelector('[data-qty-minus]');
      const plus = control.querySelector('[data-qty-plus]');

      const sync = () => {
        const value = Math.max(1, parseInt(input.value, 10) || 1);
        input.value = value;
        minus.disabled = value <= 1;
        control.dispatchEvent(new CustomEvent('tapn:quantity', { detail: value, bubbles: true }));
      };

      minus.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) - 1; sync(); });
      plus.addEventListener('click', () => { input.value = (parseInt(input.value, 10) || 1) + 1; sync(); });
      input.addEventListener('change', sync);
      sync();
    });
  }

  // ─── Quantités groupées ────────────────────────────────────────────────
  // Une ligne posée = la quantité saute au seuil du palier ; ensuite c'est le
  // champ quantité qui pilote, via l'évènement `tapn:quantity` déjà émis par
  // initQuantity. Aucun état propre : la ligne active est recalculée à chaque
  // évènement, jamais mémorisée depuis le dernier clic. Recliquer la ligne
  // déjà active désélectionne : la quantité retombe à 1, sinon une ligne
  // pourrait s'éteindre visuellement pendant que Shopify applique quand même
  // sa remise sur la quantité restée en place.
  function initBundles(root) {
    root.querySelectorAll('[data-bundle-group]:not([data-tapn-bundle-bound])').forEach((group) => {
      group.dataset.tapnBundleBound = 'true';
      const scope = group.closest('form') ?? document;
      const input = scope.querySelector('[data-qty-input]');
      if (!input) return;

      const tiers = [...group.querySelectorAll('[data-bundle-row]')]
        .map((row) => ({ row, threshold: parseInt(row.dataset.bundleQty, 10) }))
        .sort((a, b) => b.threshold - a.threshold);

      const tierFor = (qty) => tiers.find((tier) => qty >= tier.threshold) ?? null;

      const render = (qty) => {
        const tier = tierFor(qty);
        tiers.forEach(({ row, threshold }) => {
          const active = !!tier && threshold === tier.threshold;
          row.classList.toggle('is-active', active);
          row.setAttribute('aria-pressed', String(active));
        });
      };

      tiers.forEach(({ row, threshold }) => {
        row.addEventListener('click', () => {
          const alreadyActive = row.classList.contains('is-active');
          input.value = alreadyActive ? 1 : threshold;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      scope.addEventListener('tapn:quantity', (event) => render(event.detail));
      render(parseInt(input.value, 10) || 1);
    });
  }

  // ─── Barre d'achat collante ───────────────────────────────────────────
  // Masquée tant que le bouton principal est visible, elle glisse depuis le bas
  // dès qu'il sort de l'écran, pour garder l'achat accessible pendant la lecture.
  function initStickyBar(root) {
    root.querySelectorAll('[data-sticky-bar]:not([data-tapn-sticky-bound])').forEach((bar) => {
      bar.dataset.tapnStickyBound = 'true';
      const scope = bar.closest('[data-tapn-home]') ?? document;
      const mainCta = scope.querySelector('[data-main-cta]');
      const form = scope.querySelector('form[action*="/cart/add"]');
      const total = bar.querySelector('[data-sticky-total]');
      const addBtn = bar.querySelector('[data-sticky-add]');
      const unitPrice = Number(bar.dataset.unitPrice) || 0;
      if (!mainCta || !form) return;

      // ── Placement ────────────────────────────────────────────────────────
      // La barre devient enfant direct du <body>, juste après sa bande
      // d'atterrissage. Un élément « sticky » ne coulisse que dans la hauteur de
      // son parent : dans sa section d'origine, ou dans une boîte à sa taille, il
      // n'a aucune marge de manœuvre et ne se décolle jamais. Sur le <body>, il
      // dispose de toute la page. On lit `scope` avant, il dépend de la place
      // d'origine de la barre.
      const dock = document.getElementById('sticky-cta-dock');
      if (dock && bar.previousElementSibling !== dock) {
        document.body.insertBefore(bar, dock.nextSibling);
      }

      bar.hidden = false;

      // La bande prend la hauteur de la barre, et la barre remonte d'autant : elle
      // se pose donc exactement dessus en fin de page, sans allonger la page ni
      // laisser de bande blanche sous le pied de page.
      const syncDock = () => {
        if (!dock) return;
        dock.hidden = false;
        const height = bar.offsetHeight;
        dock.style.height = `${height}px`;
        bar.style.marginTop = `-${height}px`;
      };
      syncDock();
      window.addEventListener('resize', syncDock);
      if (typeof ResizeObserver !== 'undefined') new ResizeObserver(syncDock).observe(bar);

      // ── Quantité : la barre n'a pas de champ à elle ──────────────────────
      // Ses boutons écrivent dans le champ du formulaire, seule source de vérité.
      // Le formulaire renvoie ensuite un évènement `tapn:quantity` qui met à jour
      // l'affichage de la barre : total et compteur ne peuvent pas diverger.
      const formQty = form.querySelector('[data-qty-input]');
      const qtyValue = bar.querySelector('[data-sticky-qty-value]');
      const qtyMinus = bar.querySelector('[data-sticky-qty-minus]');
      const qtyPlus = bar.querySelector('[data-sticky-qty-plus]');

      const refresh = (quantity) => {
        if (total && unitPrice) total.textContent = formatPrice(unitPrice * quantity);
        if (qtyValue) qtyValue.textContent = quantity;
        if (qtyMinus) qtyMinus.disabled = quantity <= 1;
      };
      form.addEventListener('tapn:quantity', (event) => refresh(event.detail));

      const bump = (delta) => {
        if (!formQty) return;
        formQty.value = Math.max(1, (parseInt(formQty.value, 10) || 1) + delta);
        formQty.dispatchEvent(new Event('change', { bubbles: true }));
      };
      qtyMinus?.addEventListener('click', () => bump(-1));
      qtyPlus?.addEventListener('click', () => bump(1));

      // Premier affichage : initQuantity a déjà émis son évènement avant ce câblage.
      refresh(Math.max(1, parseInt(formQty?.value, 10) || 1));

      // ── Apparition ───────────────────────────────────────────────────────
      // Les fiches qui utilisent le layout produit partagé suivent précisément
      // la visibilité de leur CTA sur mobile : la sticky prend le relais dès
      // que le bouton sort du cadre, puis disparaît dès qu'il redevient
      // visible. Les autres gabarits et le desktop conservent la surveillance
      // historique du hero.
      if (typeof IntersectionObserver === 'undefined') return;
      const useCtaVisibility = bar.classList.contains('tapn-product-sticky--reference')
        && window.matchMedia('(max-width: 39.99rem)').matches;
      const watched = useCtaVisibility ? mainCta : (scope.querySelector('.tapn-product-hero') ?? mainCta);
      const observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 });
      observer.observe(watched);

      // Le bouton de la barre déclenche le vrai formulaire, configuration incluse.
      addBtn?.addEventListener('click', () => {
        mainCta.click();
      });
    });
  }

  // ─── Pastilles de couleur ──────────────────────────────────────────────
  // Change le champ id du formulaire (donc la variante achetée) et, si la
  // galerie porte des images par variante (data-gallery-variant-scoped),
  // filtre la galerie pour ne montrer que les vues de la couleur choisie.
  function applyGalleryVariant(gallery, variantTitle) {
    if (!gallery || !gallery.hasAttribute('data-gallery-variant-scoped')) return;
    const images = [...gallery.querySelectorAll('[data-gallery-image]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const matches = (el) => el.dataset.galleryVariant === variantTitle;
    const isCommon = (el) => el.dataset.galleryVariant === '';

    let firstMatchIndex = null;
    images.forEach((img) => {
      const isMatch = matches(img);
      if (isMatch && firstMatchIndex === null) firstMatchIndex = img.dataset.index;
    });
    if (firstMatchIndex === null) firstMatchIndex = images[0]?.dataset.index;
    images.forEach((img) => { img.hidden = img.dataset.index !== firstMatchIndex; });

    thumbs.forEach((thumb) => {
      const isMatch = matches(thumb);
      thumb.hidden = !isMatch && !isCommon(thumb);
      const active = isMatch && thumb.dataset.index === firstMatchIndex;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    gallery.querySelectorAll('[data-gallery-dot]').forEach((dot) => {
      const active = dot.dataset.index === firstMatchIndex;
      dot.hidden = !active && ![...thumbs].some((thumb) => !thumb.hidden && thumb.dataset.index === dot.dataset.index);
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function initSwatches(root) {
    root.querySelectorAll('[data-swatch-group]:not([data-tapn-swatch-bound])').forEach((group) => {
      group.dataset.tapnSwatchBound = 'true';
      const scope = group.closest('[data-tapn-home]') ?? document;
      const gallery = scope.querySelector('[data-product-gallery]');
      const input = scope.querySelector('[data-swatch-input]');
      const priceDisplay = scope.querySelector('[data-variant-price-display]');
      const bundleThumbs = [...scope.querySelectorAll('[data-bundle-thumb]')];
      const buttons = [...group.querySelectorAll('[data-swatch-option]')];

      // ─── Disponibilité du coloris sélectionné ──────────────────────────
      // Les boutons d'achat existent toujours dans la page ; seul leur état suit
      // le coloris choisi. Sans cela, un coloris épuisé laissait un bouton actif
      // dont le clic échouait en affichant « Réessayer », message trompeur qui
      // invitait le client à recommencer indéfiniment. Les gabarits dont les
      // pastilles ne portent pas l'attribut sont traités comme disponibles.
      const applyAvailability = (button) => {
        if (button.dataset.variantAvailable === undefined) return;
        const available = button.dataset.variantAvailable === 'true';
        const targets = [
          ...scope.querySelectorAll('[data-main-cta]'),
          ...document.querySelectorAll('[data-sticky-add]'),
        ];
        targets.forEach((target) => {
          target.disabled = !available;
          const text = target.querySelector('[data-add-button-label]');
          if (text) text.textContent = available ? 'Ajouter au panier' : 'Rupture de stock';
        });
      };

      const activeButton = buttons.find((b) => b.classList.contains('is-active'));
      applyGalleryVariant(gallery, activeButton?.dataset.variantTitle);

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          buttons.forEach((other) => {
            const active = other === button;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          if (input) input.value = button.dataset.variantId;
          if (priceDisplay && button.dataset.variantPrice) priceDisplay.textContent = formatPrice(button.dataset.variantPrice);
          if (button.dataset.variantThumb) bundleThumbs.forEach((img) => { img.src = button.dataset.variantThumb; });
          applyGalleryVariant(gallery, button.dataset.variantTitle);
          applyAvailability(button);
        });
      });
    });
  }

  // ─── Champ profil social ───────────────────────────────────────────────
  // Valide en direct que le lien collé pointe vers la bonne plateforme :
  // domaine reconnu → lien vers le compte, sinon message d'erreur. La validation
  // porte sur le domaine ; le profil s'ouvre dans un nouvel onglet afin de ne pas
  // interrompre la configuration du produit.
  function initSocialInput(root) {
    root.querySelectorAll('[data-social-input]:not([data-tapn-social-bound])').forEach((wrap) => {
      wrap.dataset.tapnSocialBound = 'true';
      const input = wrap.querySelector('[data-social-input-field]');
      const error = wrap.querySelector('[data-social-input-error]');
      const preview = wrap.querySelector('[data-social-input-preview]');
      const previewLabel = wrap.querySelector('[data-social-input-label]');
      const domains = (wrap.dataset.platformDomains || '').split(',').map((d) => d.trim().toLowerCase()).filter(Boolean);
      if (!input || domains.length === 0) return;

      const getLabel = (url) => {
        const segments = url.pathname.split('/').filter(Boolean);
        return (segments.at(-1) || '').replace(/^@/, '') || url.hostname.replace(/^www\./, '');
      };

      input.addEventListener('input', () => {
        const value = input.value.trim();
        error.hidden = true;
        preview.hidden = true;
        if (!value) return;

        let url;
        try { url = new URL(value); } catch { error.hidden = false; return; }

        const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
        const matches = domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
        if (!matches) { error.hidden = false; return; }

        previewLabel.textContent = `@${getLabel(url)}`;
        previewLabel.href = url.href;
        preview.hidden = false;
      });
    });
  }

  // ─── Choix de destination (fiches personnalisables) ────────────────────
  // Un menu déroulant pilote quel panneau reste actif parmi 3 : google / link /
  // none. Seuls les champs du panneau actif gardent leur attribut name actif,
  // les autres passent en disabled pour ne jamais envoyer de propriété en plus
  // dans la commande. Les champs internes de Google Places (data-google-place-*)
  // ne sont jamais touchés ici : ils restent sous le contrôle exclusif
  // d'initGooglePlaces, pour ne pas effacer une sélection déjà faite en
  // changeant de destination.
  //
  // Le panneau « link » sert 7 destinations (Trustpilot, Tripadvisor, les 4
  // réseaux, Autre) avec un seul champ : logo, exemple de lien et domaine
  // attendu changent selon la sélection, au lieu de dupliquer 7 panneaux
  // quasi identiques. « Autre » n'a ni logo ni domaine : la validation retombe
  // sur le type=url natif du champ, aucune plateforme n'est imposée.
  var DESTINATION_LINK_META = {
    trustpilot: { label: 'Trustpilot', domains: ['trustpilot.com'], placeholder: 'https://trustpilot.com/review/votre-entreprise' },
    tripadvisor: { label: 'Tripadvisor', domains: ['tripadvisor.fr', 'tripadvisor.com', 'tripadvisor.co.uk'], placeholder: 'https://tripadvisor.fr/votre-etablissement' },
    instagram: { label: 'Instagram', domains: ['instagram.com'], placeholder: 'https://instagram.com/votre-compte' },
    tiktok: { label: 'TikTok', domains: ['tiktok.com'], placeholder: 'https://tiktok.com/@votre-compte' },
    facebook: { label: 'Facebook', domains: ['facebook.com', 'fb.com'], placeholder: 'https://facebook.com/votre-page' },
    snapchat: { label: 'Snapchat', domains: ['snapchat.com'], placeholder: 'https://snapchat.com/add/votre-compte' },
    autre: { label: 'Autre', domains: [], placeholder: 'https://votre-lien.com' },
  };

  function initDestinationToggle(root) {
    root.querySelectorAll('[data-destination-toggle]:not([data-tapn-destination-bound])').forEach((wrap) => {
      wrap.dataset.tapnDestinationBound = 'true';
      const select = wrap.querySelector('[data-destination-select]');
      const panels = [...wrap.querySelectorAll('[data-destination-panel]')];
      const linkInput = wrap.querySelector('[data-destination-link-input]');
      const linkNameInput = wrap.querySelector('[data-destination-link-name]');
      const linkLogos = [...wrap.querySelectorAll('[data-destination-logo]')];
      const linkLogoGroup = wrap.querySelector('.tapn-product-config__logo-group');
      const linkError = wrap.querySelector('[data-destination-link-error]');
      const linkPreview = wrap.querySelector('[data-destination-link-preview]');
      const linkPreviewLabel = wrap.querySelector('[data-destination-link-preview-label]');
      const noneInput = wrap.querySelector('[data-destination-none-input]');
      if (!select) return;

      let activeDomains = [];

      const getLabel = (url) => {
        const segments = url.pathname.split('/').filter(Boolean);
        return (segments.at(-1) || '').replace(/^@/, '') || url.hostname.replace(/^www\./, '');
      };

      const checkLink = () => {
        if (!linkInput || !linkError || !linkPreview) return;
        const value = linkInput.value.trim();
        linkError.hidden = true;
        linkPreview.hidden = true;
        if (!value) return;
        let url;
        try { url = new URL(value); } catch { linkError.hidden = false; return; }
        if (activeDomains.length) {
          const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
          const matches = activeDomains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
          if (!matches) { linkError.hidden = false; return; }
        }
        if (linkPreviewLabel) {
          linkPreviewLabel.textContent = `@${getLabel(url)}`;
          linkPreviewLabel.href = url.href;
        }
        linkPreview.hidden = false;
      };

      linkInput?.addEventListener('input', checkLink);

      const activate = (value) => {
        const isGoogle = value === 'google';
        const isNone = value === 'none';
        const isLink = !isGoogle && !isNone;
        const meta = DESTINATION_LINK_META[value];

        panels.forEach((panel) => {
          const key = panel.dataset.destinationPanel;
          const isActive = (key === 'google' && isGoogle) || (key === 'link' && isLink) || (key === 'none' && isNone);
          panel.hidden = !isActive;
          panel.querySelectorAll('[data-destination-google-input]').forEach((field) => { field.disabled = !isActive; });
        });

        if (linkInput) linkInput.disabled = !isLink;
        if (linkNameInput) linkNameInput.disabled = !isLink;
        if (noneInput) noneInput.disabled = !isNone;

        if (isLink && meta && linkInput) {
          activeDomains = meta.domains;
          linkInput.placeholder = meta.placeholder;
          linkInput.setAttribute('aria-label', `Votre profil ${meta.label}`);
          if (linkNameInput) linkNameInput.value = meta.label;
          linkLogos.forEach((img) => { img.hidden = img.dataset.destinationLogo !== value; });
          // « Autre » n'a pas de logo : sans ce masquage, le conteneur vide restait un
          // élément flex à part entière et son gap décalait le champ de 12px vers la
          // droite, désaligné avec le menu déroulant juste au-dessus.
          if (linkLogoGroup) linkLogoGroup.hidden = !linkLogos.some((img) => img.dataset.destinationLogo === value);
          if (linkError) linkError.hidden = true;
          if (linkPreview) linkPreview.hidden = true;
          if (linkInput.value) checkLink();
        }
      };

      select.addEventListener('change', () => activate(select.value));
    });
  }

  // ─── Choix opt-out (fiches à plateforme unique) ─────────────────────────
  // 2 choix seulement, la plateforme étant déjà fixée par le gabarit : « Nous
  // configurons votre carte » garde le champ existant (Google Places ou champ
  // social selon le fichier), « Je configure moi-même » le désactive et affiche
  // le renvoi vers le guide. Même principe que initDestinationToggle mais sans
  // table de métadonnées à piloter, un seul panneau existant à basculer.
  function initConfigOptOut(root) {
    root.querySelectorAll('[data-optout-toggle]:not([data-tapn-optout-bound])').forEach((wrap) => {
      wrap.dataset.tapnOptoutBound = 'true';
      const select = wrap.querySelector('[data-optout-select]');
      const panels = [...wrap.querySelectorAll('[data-optout-panel]')];
      const noneInput = wrap.querySelector('[data-optout-none-input]');
      if (!select) return;

      const activate = (value) => {
        const isNone = value === 'none';
        panels.forEach((panel) => {
          const isActive = (panel.dataset.optoutPanel === 'config' && !isNone) || (panel.dataset.optoutPanel === 'none' && isNone);
          panel.hidden = !isActive;
          panel.querySelectorAll('[data-optout-google-input],[data-optout-social-input]').forEach((field) => { field.disabled = !isActive; });
        });
        if (noneInput) noneInput.disabled = !isNone;
      };

      select.addEventListener('change', () => activate(select.value));
    });
  }

  // ─── Recherche d'établissement Google Maps ───────────────────────────
  // Le composant Places officiel remplace le champ libre quand la clé navigateur
  // est configurée. Les propriétés ajoutées au panier donnent à l'atelier le nom,
  // l'adresse et l'identifiant exact du lieu sélectionné.
  let googleMapsScript;

  function loadGoogleMaps(apiKey) {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    if (googleMapsScript) return googleMapsScript;

    googleMapsScript = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&language=fr&region=FR`;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Impossible de charger Google Maps'));
      document.head.append(script);
    });
    return googleMapsScript;
  }

  function getGooglePlaceText(value) {
    return typeof value === 'string' ? value : value?.text || '';
  }

  function renderGoogleStars(container, rating) {
    container.replaceChildren();
    for (let index = 0; index < 5; index += 1) {
      const fill = Math.max(0, Math.min(1, rating - index));
      const star = document.createElement('span');
      star.className = 'tapn-google-places__star';
      star.style.setProperty('--tapn-star-fill', `${fill * 100}%`);
      star.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2.8 2.83 5.74 6.34.92-4.58 4.47 1.08 6.31L12 17.26l-5.67 2.98 1.08-6.31-4.58-4.47 6.34-.92L12 2.8Z"/></svg><span class="tapn-google-places__star-fill"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2.8 2.83 5.74 6.34.92-4.58 4.47 1.08 6.31L12 17.26l-5.67 2.98 1.08-6.31-4.58-4.47 6.34-.92L12 2.8Z"/></svg></span>';
      container.append(star);
    }
  }

  function initGooglePlaces(root) {
    root.querySelectorAll('[data-google-places]:not([data-tapn-google-places-bound])').forEach(async (wrap) => {
      wrap.dataset.tapnGooglePlacesBound = 'true';
      const apiKey = wrap.dataset.apiKey;
      const fallback = wrap.querySelector('[data-google-places-fallback]');
      const host = wrap.querySelector('[data-google-places-host]');
      const status = wrap.querySelector('[data-google-places-status]');
      const error = wrap.querySelector('[data-google-places-error]');
      const nameInput = wrap.querySelector('[data-google-place-name]');
      const addressInput = wrap.querySelector('[data-google-place-address]');
      const idInput = wrap.querySelector('[data-google-place-id]');
      const selection = wrap.querySelector('[data-google-places-selection]');
      const displayName = wrap.querySelector('[data-google-place-display-name]');
      const meta = wrap.querySelector('[data-google-place-meta]');
      const ratingDisplay = wrap.querySelector('[data-google-place-rating]');
      const stars = wrap.querySelector('[data-google-place-stars]');
      const reviewCount = wrap.querySelector('[data-google-place-review-count]');
      const categoryRow = wrap.querySelector('[data-google-place-category-row]');
      const category = wrap.querySelector('[data-google-place-category]');
      const accessibilitySeparator = wrap.querySelector('[data-google-place-accessibility-separator]');
      const accessibility = wrap.querySelector('[data-google-place-accessibility]');
      if (!apiKey || !fallback || !host || !nameInput || !addressInput || !idInput) return;
      let hasSelectedPlace = false;
      let autocomplete = null;

      const restoreFallback = (message) => {
        fallback.hidden = false;
        fallback.disabled = false;
        host.replaceChildren();
        wrap.classList.remove('is-ready');
        if (status) status.hidden = true;
        if (error) { error.textContent = message; error.hidden = false; }
      };
      const clearSelection = () => {
        nameInput.value = '';
        addressInput.value = '';
        idInput.value = '';
        nameInput.disabled = true;
        addressInput.disabled = true;
        idInput.disabled = true;
        selection.hidden = true;
        hasSelectedPlace = false;
      };

      const getAutocompleteValue = () => {
        if (typeof autocomplete.value === 'string') return autocomplete.value.trim();
        const internalInput = autocomplete.shadowRoot?.querySelector('input');
        return typeof internalInput?.value === 'string' ? internalInput.value.trim() : null;
      };

      const clearRemovedSelection = () => {
        window.setTimeout(() => {
          if (getAutocompleteValue() === '') clearSelection();
        });
      };

      try {
        if (status) { status.textContent = 'Chargement de la recherche…'; status.hidden = false; }
        await loadGoogleMaps(apiKey);
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
        autocomplete = new PlaceAutocompleteElement({
          includedRegionCodes: ['fr'],
          includedPrimaryTypes: ['establishment'],
        });
        autocomplete.className = 'tapn-google-places__widget';
        autocomplete.style.colorScheme = 'light';
        autocomplete.setAttribute('aria-label', 'Rechercher un établissement');
        if (wrap.dataset.placeholder) autocomplete.placeholder = wrap.dataset.placeholder;
        const clearIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        clearIcon.classList.add('tapn-google-places__clear-icon');
        clearIcon.setAttribute('slot', 'clear-icon');
        clearIcon.setAttribute('viewBox', '0 0 24 24');
        clearIcon.setAttribute('aria-hidden', 'true');
        clearIcon.innerHTML = '<path d="M6 6l12 12M18 6 6 18"/>';
        autocomplete.append(clearIcon);
        const clearSelectedPlace = () => {
          clearSelection();
          window.setTimeout(clearSelection);
        };
        clearIcon.addEventListener('pointerdown', clearSelectedPlace);
        clearIcon.addEventListener('click', clearSelectedPlace);
        autocomplete.addEventListener('pointerdown', (event) => {
          if (event.composedPath().includes(clearIcon)) clearSelectedPlace();
        }, true);
        host.append(autocomplete);
        fallback.hidden = true;
        fallback.disabled = true;
        wrap.classList.add('is-ready');
        if (status) status.hidden = true;

        autocomplete.addEventListener('gmp-error', () => {
          restoreFallback('Google n’autorise pas encore la recherche depuis cette boutique. Vous pouvez saisir le nom de l’établissement manuellement.');
        }, { once: true });
        autocomplete.addEventListener('input', () => {
          if (hasSelectedPlace) clearSelection();
        });
        ['change', 'keyup', 'cut', 'paste', 'click'].forEach((eventName) => {
          autocomplete.addEventListener(eventName, clearRemovedSelection);
        });
        wrap.closest('form')?.addEventListener('submit', () => {
          if (getAutocompleteValue() === '') clearSelection();
        });
        const valueMonitor = window.setInterval(() => {
          const currentValue = getAutocompleteValue();
          if (hasSelectedPlace && currentValue === '') clearSelection();
        }, 150);
        document.addEventListener('shopify:section:unload', (sectionEvent) => {
          if (sectionEvent.target.contains(wrap)) window.clearInterval(valueMonitor);
        });
        autocomplete.addEventListener('gmp-select', async (event) => {
          clearSelection();
          if (error) error.hidden = true;
          if (status) { status.textContent = 'Chargement de l’établissement…'; status.hidden = false; }
          try {
            const prediction = event.placePrediction || event.detail?.placePrediction;
            const place = prediction?.toPlace();
            if (!place) throw new Error('Établissement introuvable');
            await place.fetchFields({ fields: ['id', 'displayName', 'formattedAddress', 'primaryTypeDisplayName', 'rating', 'userRatingCount', 'accessibilityOptions'] });
            const name = getGooglePlaceText(place.displayName);
            const address = place.formattedAddress || '';
            nameInput.value = name;
            addressInput.value = address;
            idInput.value = place.id || '';
            nameInput.disabled = false;
            addressInput.disabled = false;
            idInput.disabled = false;
            displayName.textContent = name;
            const rating = Number(place.rating);
            const count = Number(place.userRatingCount);
            const hasRating = Number.isFinite(rating) && rating > 0;
            meta.hidden = !hasRating;
            if (hasRating) {
              const localizedRating = rating.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
              ratingDisplay.textContent = localizedRating;
              stars.setAttribute('aria-label', `Note de ${localizedRating} sur 5`);
              renderGoogleStars(stars, rating);
              reviewCount.textContent = Number.isFinite(count) ? `(${count.toLocaleString('fr-FR')})` : '';
            }
            const categoryText = getGooglePlaceText(place.primaryTypeDisplayName);
            const wheelchairAccessible = place.accessibilityOptions?.hasWheelchairAccessibleEntrance === true;
            category.textContent = categoryText;
            categoryRow.hidden = !categoryText && !wheelchairAccessible;
            accessibility.hidden = !wheelchairAccessible;
            accessibilitySeparator.hidden = !categoryText || !wheelchairAccessible;
            selection.hidden = false;
            hasSelectedPlace = true;
          } catch (selectionError) {
            if (error) { error.textContent = 'Cet établissement n’a pas pu être chargé. Réessayez ou saisissez son nom manuellement.'; error.hidden = false; }
            fallback.hidden = false;
            fallback.disabled = false;
          } finally {
            if (status) status.hidden = true;
          }
        });
      } catch (loadError) {
        restoreFallback('La recherche Google est momentanément indisponible. Vous pouvez saisir le nom de l’établissement.');
      }
    });
  }

  // ─── Ajout au panier sans navigation ────────────────────────────────
  // Shopify reçoit le formulaire natif, propriétés de ligne incluses. Le panier
  // retourné est transmis au header afin de rafraîchir immédiatement son compteur
  // et son tiroir sans quitter la fiche produit.
  function initAjaxAddToCart(root) {
    root.querySelectorAll('[data-tapn-google-product] form[action*="/cart/add"]:not([data-tapn-ajax-add-bound])').forEach((form) => {
      const mainButton = form.querySelector('[data-main-cta]');
      if (!mainButton) return;
      form.dataset.tapnAjaxAddBound = 'true';
      let busy = false;

      const feedbackButtons = () => [mainButton, ...document.querySelectorAll('[data-sticky-add]')];
      const setButtonState = (state, label) => {
        feedbackButtons().forEach((button) => {
          button.classList.toggle('is-loading', state === 'loading');
          button.classList.toggle('is-success', state === 'success');
          button.disabled = state === 'loading' || state === 'success';
          const text = button.querySelector('[data-add-button-label]');
          if (text) text.textContent = label;
        });
      };

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (busy) return;
        busy = true;
        setButtonState('loading', 'Ajout en cours');
        await Promise.resolve();

        const rootPath = window.Shopify?.routes?.root || '/';
        try {
          const addResponse = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          if (!addResponse.ok) throw new Error('Cart add failed');

          try {
            const cartResponse = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
            if (!cartResponse.ok) throw new Error('Cart refresh failed');
            const cart = await cartResponse.json();
            document.dispatchEvent(new CustomEvent('tapn:cart-updated', { detail: { cart } }));
          } catch {
            document.dispatchEvent(new CustomEvent('tapn:cart-updated'));
          }
          setButtonState('success', 'Ajouté au panier');
          await new Promise((resolve) => window.setTimeout(resolve, 1200));
          setButtonState('idle', 'Ajouter au panier');
        } catch {
          setButtonState('idle', 'Réessayer');
          await new Promise((resolve) => window.setTimeout(resolve, 1600));
          setButtonState('idle', 'Ajouter au panier');
        } finally {
          busy = false;
        }
      });
    });
  }

  // ─── Fenêtres (compatibilité, questions fréquentes) ───────────────────
  // On s'appuie sur <dialog> : le navigateur gère seul la fermeture au clavier,
  // le fond bloquant et le piège de focus. Il ne reste que l'ouverture à câbler.
  function initDialogs(root) {
    root.querySelectorAll('[data-dialog-open]:not([data-tapn-dialog-bound])').forEach((trigger) => {
      trigger.dataset.tapnDialogBound = 'true';
      const dialog = document.getElementById(trigger.dataset.dialogOpen);
      if (!dialog) return;
      dialog.setAttribute('scroll-lock', '');

      trigger.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-dialog-close]')?.addEventListener('click', () => dialog.close());
      // Clic en dehors du panneau : le clic tombe sur le <dialog> lui-même.
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    });
  }

  // ─── Accordéon des questions ──────────────────────────────────────────
  // Ouvrir une question referme les autres, pour garder la liste lisible.
  function initFaqAccordion(root) {
    root.querySelectorAll('.tapn-dialog .tapn-faq-list:not([data-tapn-faq-bound])').forEach((list) => {
      list.dataset.tapnFaqBound = 'true';
      const items = [...list.querySelectorAll('details')];
      items.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          items.forEach((other) => { if (other !== item) other.open = false; });
        });
      });
    });
  }

  // ─── Libellé du lien de paiement natif Shopify ─────────────────────────
  // « Plus de moyens de paiement » n'est pas un texte à nous : Shopify
  // l'injecte lui-même une fois le composant de paiement rapide rendu, texte
  // non exposé côté Admin ni dans les locales du thème. Même technique que
  // le renommage du bouton cookies (tapn-cookie-banner.js) : réécrire le
  // texte réel une fois présent, jamais un habillage CSS, pour que le
  // lecteur d'écran annonce le même mot que ce qui est visible.
  function initMorePaymentOptionsLabel() {
    var LABEL = 'Autres moyens de paiement';

    function relabel(link) {
      if (link && link.textContent !== LABEL) link.textContent = LABEL;
    }

    var existing = document.getElementById('more-payment-options-link');
    if (existing) { relabel(existing); return; }

    var observer = new MutationObserver(function () {
      var link = document.getElementById('more-payment-options-link');
      if (link) {
        relabel(link);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initAll(root = document) {
    initGallery(root);
    initQuantity(root);
    initBundles(root);
    initStickyBar(root);
    initSwatches(root);
    initSwatchGalleryJump(root);
    initSocialInput(root);
    initGooglePlaces(root);
    initDestinationToggle(root);
    initConfigOptOut(root);
    initAjaxAddToCart(root);
    initDialogs(root);
    initFaqAccordion(root);
    initMorePaymentOptionsLabel();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
